import { IProfile } from "global-types/global.types";
import Head from "next/head";
import styled from "styled-components";
import FavoriteBg from "@/assets/img/favorite.png";
import Link from "next/link";
import CartTable from "@/components/Ui/Pages/CartTable/CartTable";
import useGetBasketData from "@/hooks/common/useGetBasketData";
import EmptyCart from "@/components/Ui/Pages/EmptyCart/EmptyCart";
import AccordionCart from "@/components/Ui/Tools/AccordionCart/AccordionCart";
import RenderSvg from "@/components/Svg/RenderSvg";
import { t, localePrefix } from "@/helper/helper";
import { LIGHT_BLUR_DATA_URL, SITE_NAME } from "@/helper/constant";
import Image from "next/image";
import { useMedia } from "react-use-media";
import useSettings from "@/hooks/user/useSettings";
import useSettingsAll from "@/hooks/common/useSettingsAll";
import { MEDIA_URL } from "@/services/api";

interface Props {
  locale: string;
  profile: IProfile;
}

export default function Basket({ locale, profile }: Props) {
  const { basketData: initialData, isLoadingBasketData } = useGetBasketData();
  const { settings } = useSettingsAll();
  const isMobile = useMedia({
    maxWidth: 767.9,
  });

  return (
    <Wrapper>
      <Head>
        <title>{SITE_NAME + " " + t("myCart", locale)}</title>
      </Head>
      <div className="cart-page">
        <div className="cart-header">
          <div className="breadcrumb">
            <Link href={`${localePrefix(locale) || "/"}`}>{t("homePage", locale)}</Link>
            <span className="breadcrumb-seperator" />
            <Link href={`${localePrefix(locale)}/basket`}>{t("myCart", locale)}</Link>
          </div>
          {initialData?.data?.items && initialData?.data.items.length !== 0 ? (
            <div className="info">
              <h1 className="title">{t("myCart", locale)}</h1>
              <p className="customer-code">
                {initialData?.data?.items.length} {t("product", locale)}
              </p>
            </div>
          ) : (
            <></>
          )}
        </div>
        {settings?.data.is_basket_banner && (
          <CartBanner href={settings?.data.basket_banner_url || ""}>
            <img
              src={
                MEDIA_URL +
                "/" +
                (isMobile
                  ? settings?.data.basket_banner_mob || ""
                  : settings?.data.basket_banner_web || "")
              }
              alt="Cart banner"
            />
          </CartBanner>
        )}
        {(!isLoadingBasketData && initialData?.data?.items === undefined) ||
        initialData?.data.items.length === 0 ? (
          <EmptyCart locale={locale} login={profile ? true : false} />
        ) : (
          <div className="cart-container">
            <div className="main-section">
              {initialData?.data?.items.map((item, index) => (
                <CartTable
                  data={item}
                  locale={locale}
                  key={index.toString() + item.product.title}
                />
              ))}
              <div className="cardEnd">
                <div className="cardEnd__flexBox">
                  <div className="cardEnd__left">
                    <img src="https://images.static-thomann.de/pics//images/basket/shipping-truck.png" />
                    <div className="cardEnd__text">
                      <p>{t("delivery", locale)}</p>

                      {/* {!isLoadingCardList &&
              cardList.data.data.shipping_cost !== undefined ? (
                <p>Shipping costs:</p>
              ) : (
                <></>
              )} */}
                    </div>
                  </div>
                  <div className="cardEnd__right">
                    {initialData?.data?.shipping_cost !== undefined ? (
                      <p className="cardEnd__content">
                        {initialData?.data?.shipping_cost}
                        <svg
                          width={33}
                          height={27}
                          viewBox="0 0 33 27"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="cardEnd__manat"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M15 26L15.8511 1.03704L18.6596 0L19 24L15 26Z"
                            fill="#FF4C4C"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M0 26.7126L3.31441 26.8563C3.37837 10.7044 12.0742 5.37873 16.3559 5.22923C27.9253 5.59751 29.2515 20.3484 29.6856 26.8563L33 27C32.7507 11.4088 24.1817 2.85531 17.1485 3.00185C5.15613 3.42337 0.588832 15.6295 0 26.7126Z"
                            fill="#FF4C4C"
                          />
                        </svg>
                      </p>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="side-section">
              <h1 className="title">{t("doPayment", locale)}</h1>
              <div className="wrapper">
                <div className="cart-calculations">
                  <div className="total">
                    <p className="left">{t("costOfProducts", locale)}</p>
                    <p className="right">
                      {initialData?.data &&
                        initialData?.data?.total -
                          initialData?.data.discount}{" "}
                      <svg
                        className="manatIcon"
                        width={33}
                        height={27}
                        viewBox="0 0 33 27"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M15 26L15.8511 1.03704L18.6596 0L19 24L15 26Z"
                          fill="#FF4C4C"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M0 26.7126L3.31441 26.8563C3.37837 10.7044 12.0742 5.37873 16.3559 5.22923C27.9253 5.59751 29.2515 20.3484 29.6856 26.8563L33 27C32.7507 11.4088 24.1817 2.85531 17.1485 3.00185C5.15613 3.42337 0.588832 15.6295 0 26.7126Z"
                          fill="#FF4C4C"
                        />
                      </svg>
                    </p>
                  </div>
                  <div className="discount">
                    <p className="left">{t("delivery", locale)}</p>
                    <p className="right">
                      {initialData?.data?.shipping_cost}{" "}
                      <svg
                        className="manatIcon"
                        width={33}
                        height={27}
                        viewBox="0 0 33 27"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M15 26L15.8511 1.03704L18.6596 0L19 24L15 26Z"
                          fill="#FF4C4C"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M0 26.7126L3.31441 26.8563C3.37837 10.7044 12.0742 5.37873 16.3559 5.22923C27.9253 5.59751 29.2515 20.3484 29.6856 26.8563L33 27C32.7507 11.4088 24.1817 2.85531 17.1485 3.00185C5.15613 3.42337 0.588832 15.6295 0 26.7126Z"
                          fill="#FF4C4C"
                        />
                      </svg>
                    </p>
                  </div>
                </div>
                <div className="total-amount">
                  <div className="total-amount-text">
                    <p className="left">{t("total", locale)}:</p>
                    <div className="right">
                      {initialData?.data?.payed}{" "}
                      <svg
                        className="manatIcon"
                        width={33}
                        height={27}
                        viewBox="0 0 33 27"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M15 26L15.8511 1.03704L18.6596 0L19 24L15 26Z"
                          fill="#FF4C4C"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M0 26.7126L3.31441 26.8563C3.37837 10.7044 12.0742 5.37873 16.3559 5.22923C27.9253 5.59751 29.2515 20.3484 29.6856 26.8563L33 27C32.7507 11.4088 24.1817 2.85531 17.1485 3.00185C5.15613 3.42337 0.588832 15.6295 0 26.7126Z"
                          fill="#FF4C4C"
                        />
                      </svg>
                    </div>
                  </div>
                  <Link
                    className="make-order-btn"
                    href={profile ? `${localePrefix(locale)}/payment` : `${localePrefix(locale)}/guest`}
                  >
                    {t("order", locale)}
                  </Link>
                  {/* {!isLoggedIn && (
            <>
              <span className="login-span">{t("orderWarning")}</span>
              <button
                className="guest-order-btn"
                onClick={() => history.push("/guest")}
              >
                {t("buyAsGuest")}
              </button>
            </>
          )} */}
                </div>
                <div className="accordion-container">
                  <AccordionCart
                    icon={<RenderSvg title="package" />}
                    version="acc-side"
                    title={t("secureOnlinePayment", locale)}
                    open={false}
                  >
                    {t("secureOnlinePayment", locale)}
                  </AccordionCart>
                  <AccordionCart
                    icon={<RenderSvg title="security" />}
                    version="acc-side"
                    title={t("secureDeliveryByMail", locale)}
                    open={false}
                  >
                    {t("secureDeliveryByMail", locale)}
                  </AccordionCart>
                  <AccordionCart
                    icon={<RenderSvg title="guarantee" />}
                    version="acc-side"
                    title={t("warrantyAndQuality", locale)}
                    open={false}
                  >
                    {t("warrantyAndQuality", locale)}
                  </AccordionCart>
                  <AccordionCart
                    icon={<RenderSvg title="guarantee" />}
                    version="acc-side"
                    title={t("refund", locale)}
                    open={false}
                  >
                    {t("refundContent", locale)}
                  </AccordionCart>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
}

const CartBanner = styled(Link)`
  display: block;
  width: 100%;
  border-radius: 6px;
  margin-bottom: 3.6rem;

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    height: 200px;
  }

  @media screen and (max-width: 767.9px) {
    img {
      height: 100px;
    }
  }
`;

const Wrapper = styled.div`
  .favorite-page {
    width: 1128px;
    margin: 0 auto;
    margin-top: 2.4rem;
    padding-bottom: 7rem;

    .breadcrumb {
      display: none;
    }

    @media screen and (max-width: 1199px) {
      width: 100%;
      padding: 0 16px;
    }

    .favorite-header {
      background: url(${FavoriteBg.src});
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center center;
      width: 100%;
      height: 27rem;
      border-radius: 6px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: space-between;
      padding: 2.4rem;

      .breadcrumb {
        padding-top: 0 !important;
        display: block;

        a {
          color: #fff;
        }

        .breadcrumb-seperator {
          background-color: #fff;
        }
      }

      .info {
        .title {
          font-style: normal;
          font-weight: 600;
          font-size: 3.2rem;
          line-height: 100%;
          color: #ffffff;
          margin-bottom: 6px;
        }

        .customer-code {
          font-style: normal;
          font-weight: normal;
          font-size: 1.2rem;
          line-height: 125%;
          color: #fdfdfd;
        }
      }
    }

    .favorite-container {
      margin-top: 5.6rem;
      width: 100%;
      height: auto;
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
      grid-gap: 3.6rem 2.4rem;
    }
  }

  @media screen and (max-width: 767.9px) {
    .favorite-page {
      .breadcrumb {
        display: block;
        margin-bottom: 24px;
        padding-top: 0 !important;
      }

      .favorite-header {
        height: 150px;
        justify-content: flex-end;

        .breadcrumb {
          display: none;
        }

        .customer-code {
          font-size: 14px !important;
        }
      }

      .favorite-container {
        margin: 42px 0;
      }
    }
  }
`;
